/**
 * Hero — product-first edition.
 *
 * Structure (top → bottom):
 *   - Constellation backdrop (Tier 3 ambient)
 *   - Wordmark "NYX" (top-left)
 *   - Stage:
 *       Headline (word-by-word stagger, replays on `key={themeKey}` remount)
 *       Sub-hero paragraph
 *       Closer + availability ("En español, sin diluir. Pronto en iOS.")
 *   - Album cover slot (desktop: position:absolute pins it to the top-right
 *     corner and the nl-cover-in keyframe lands it first on swap.
 *     Phones: position:static drops it into the flow between the stage and
 *     the bottom row — see hero.css @media (max-width: 700px))
 *   - Bottom row:
 *       Secondary deep-dive link (small) — sample-content entry point
 *       Primary CTA button (waitlist while pre-launch, App Store post-launch)
 *
 * Launch-day swap: change VITE_LAUNCH_PHASE=live in the Cloudflare Pages
 * dashboard. The CTA + the availability phrase flip; nothing else moves.
 */

import { Fragment, useMemo, type ReactNode } from 'react';
import { Constellation } from './Constellation';
import { Cover } from './Picker/Cover';
import { LAUNCH_PHASE, heroCopy } from '@/copy/hero';
import { useTrack } from '@/analytics';
import type { AvailableAlbumModule } from '@/types/album';

interface HeroProps {
  album: AvailableAlbumModule;
}

interface StaggerWord {
  text: string;
  delay: number;
}

const STEP_MS = 110;
const START_MS = 600;
const DEFAULT_HEADLINE = 'Significados de las canciones que ya cantas de memoria.';
const DEFAULT_CTA = {
  eyebrow: 'Lista de espera',
  label: 'Avísame cuando esté listo',
  href: '#waitlist',
};
const DEFAULT_AVAILABILITY = 'Pronto en iOS.';
const DEFAULT_SUBHERO =
  'Una app para fans que escuchan de cerca. Cómo se mueve un álbum, qué dice cada canción, qué cambia cuando lo ves en vivo.';
const DEFAULT_CLOSER = 'En español.';
const DEFAULT_DEEP_DIVE_LINK = (albumTitle: string): string => `Lee el análisis de ${albumTitle}`;

function buildStagger(text: string): StaggerWord[] {
  return text.split(/\s+/).map((word, i) => ({
    text: word,
    delay: START_MS + i * STEP_MS,
  }));
}

export function Hero({ album }: HeroProps): JSX.Element {
  const { library } = album;
  const headline = heroCopy?.headline ?? DEFAULT_HEADLINE;
  const stagger = useMemo(() => buildStagger(headline), [headline]);
  const cta = heroCopy?.cta?.[LAUNCH_PHASE] ?? DEFAULT_CTA;
  const availability = heroCopy?.availability?.[LAUNCH_PHASE] ?? DEFAULT_AVAILABILITY;
  const subhero = heroCopy?.subhero ?? DEFAULT_SUBHERO;
  const closer = heroCopy?.closer ?? DEFAULT_CLOSER;
  const deepDiveLink = heroCopy?.deepDiveLink?.(library.album) ?? DEFAULT_DEEP_DIVE_LINK(library.album);
  const track = useTrack();
  // Sub-hero entrance starts as the headline finishes (overlap is intentional).
  const subStartMs = START_MS + stagger.length * STEP_MS;
  const ctaKind = LAUNCH_PHASE === 'waitlist' ? 'waitlist' : 'app_store';

  return (
    <section className="hero" aria-label={`Hero · Nyx · ${library.album}`}>
      <div className="hero-bg" aria-hidden="true">
        <Constellation density={70} drawLines={false} seed={3} glow />
      </div>
      <div className="hero-noise" aria-hidden="true" />

      <div className="hero-content">
        <div className="stage">
          <h1 className="gap-sentence">
            {stagger.map((w, i) => {
              const inner: ReactNode = w.text;
              return (
                <Fragment key={i}>
                  <span
                    className="gap-word"
                    style={{ animationDelay: `${w.delay}ms` }}
                  >
                    {inner}
                  </span>
                  {i < stagger.length - 1 ? ' ' : ''}
                </Fragment>
              );
            })}
          </h1>

          <p
            className="nl-hero-sub nl-hero-fade"
            style={{ animationDelay: `${subStartMs}ms` }}
          >
            {subhero}
          </p>

          <p
            className="nl-hero-availability nl-hero-fade"
            style={{ animationDelay: `${subStartMs + 220}ms` }}
          >
            {closer}{' '}
            <span
              className="nl-hero-launch-phase"
              data-phase={LAUNCH_PHASE}
              data-text={availability}
            >
              {availability}
            </span>
          </p>
        </div>

        {/* Desktop pins this to the top-right corner via position:absolute
            (see hero.css). Phones leave it in flow so it sits between the
            stage and the bottom row. */}
        <div className="nl-hero-cover-wrap">
          <Cover album={library} size={220} loading="eager" />
          <div className="nl-hero-cover-eyebrow">Disco analizado</div>
        </div>

        <div
          className="nl-hero-bottom nl-hero-fade"
          style={{ animationDelay: `${subStartMs + 440}ms` }}
        >
          <a
            className="nl-hero-deep-dive"
            href="#deep-dive"
            onClick={() => track('Hero CTA Click', { kind: 'deep_dive' })}
          >
            <span className="glyph" aria-hidden="true">
              ↓
            </span>
            <span className="label">{deepDiveLink}</span>
          </a>

          <a
            className="nl-hero-cta"
            href={cta.href}
            onClick={() => track('Hero CTA Click', { kind: ctaKind })}
          >
            <div className="nl-hero-cta-meta">
              <div className="nl-hero-cta-eyebrow">{cta.eyebrow}</div>
              <div className="nl-hero-cta-title">{cta.label}</div>
            </div>
            <div className="nl-hero-cta-arrow" aria-hidden="true">
              <span className="nl-hero-cta-arrow-track">
                <span className="nl-hero-cta-arrow-glyph">→</span>
                <span className="nl-hero-cta-arrow-glyph nl-hero-cta-arrow-ghost">
                  →
                </span>
              </span>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
